import { useEffect, useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";
import { Warning, Archive, Trash, ShieldWarning } from "@phosphor-icons/react";

// Shared destructive-delete flow used by Admin → Data & Cleanup AND Asset Detail, so both obey
// exactly the same permission + dependency rules. Fetches the dependency impact, shows valid
// choices only, and enforces Super Admin + reason + type-DELETE for protected records.
export default function RecordDeleteDialog({ entity, id, label, isSuperAdmin, onClose, onDeleted }) {
  const [impact, setImpact] = useState(null);
  const [reason, setReason] = useState("");
  const [confirmTxt, setConfirmTxt] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/admin/records/${entity}/${id}/impact`).then((r) => setImpact(r.data)).catch((e) => {
      toast.error(e.response?.data?.detail || "Could not load impact"); onClose();
    });
  }, [entity, id, onClose]);

  if (!impact) return null;
  const protectedRec = impact.protected;
  const blockedNoSuper = protectedRec && !isSuperAdmin;
  const canConfirm = !protectedRec || (reason.trim() && confirmTxt.trim().toUpperCase() === "DELETE");

  const run = async (mode) => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/records/${entity}/${id}/delete`, { mode, reason: reason.trim(), confirm: confirmTxt.trim() });
      const extra = r.data.total ? ` (+${r.data.total} linked)` : "";
      toast.success(`Permanently deleted${extra}`);
      onDeleted();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const archive = async () => {
    setBusy(true);
    try { await api.post(`/admin/records/${entity}/${id}/archive`); toast.success("Archived instead"); onDeleted(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="record-delete-dialog" className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <Warning size={20} weight="bold" />{impact.total_dependents > 0 ? "Permanently delete + linked data?" : "Permanently delete this record?"}
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-slate-700">You are about to delete <b>{label || impact.label}</b>.</p>

        {impact.total_dependents > 0 && (
          <div data-testid="dependency-summary" className="rounded-lg border-2 border-amber-300 bg-amber-50 p-3">
            <div className="text-xs font-black uppercase text-amber-800 mb-1">This will also permanently remove:</div>
            <ul className="text-sm text-amber-900 list-disc pl-5">
              {impact.dependents.map((d) => <li key={d.label} data-testid={`dep-${d.label.replace(/\s+/g, "-")}`}>{d.count} {d.label}</li>)}
              <li className="text-amber-700/70">related status/history records</li>
            </ul>
          </div>
        )}

        {protectedRec && (
          <div data-testid="irreversible-warning" className="flex items-start gap-2 rounded bg-red-600 text-white px-3 py-2 text-sm font-semibold">
            <ShieldWarning size={18} weight="bold" className="mt-0.5 shrink-0" />
            This is protected historical/compliance data. Permanent deletion is irreversible. A lightweight deletion record (who/when/why) is kept as evidence.
          </div>
        )}

        {blockedNoSuper ? (
          <div data-testid="needs-super-admin" className="text-sm font-bold text-red-700 bg-red-50 border border-red-200 rounded p-2">
            Super Admin permission is required to permanently delete this record. You can archive it instead.
          </div>
        ) : protectedRec && (
          <div className="flex flex-col gap-2">
            <div>
              <label className="text-xs font-bold uppercase text-slate-500">Reason (required)</label>
              <Textarea data-testid="delete-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why is this being permanently deleted?" className="mt-1" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-slate-500">Type DELETE to confirm</label>
              <Input data-testid="delete-confirm-input" value={confirmTxt} onChange={(e) => setConfirmTxt(e.target.value)} placeholder="DELETE" className="mt-1 h-9" />
            </div>
          </div>
        )}

        <div className="flex flex-wrap justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} data-testid="delete-cancel">Cancel</Button>
          {impact.can_archive && (
            <Button data-testid="delete-archive" disabled={busy} onClick={archive} className="bg-slate-700 hover:bg-slate-800 font-semibold"><Archive size={15} weight="bold" className="mr-1" />Archive instead</Button>
          )}
          {!blockedNoSuper && impact.actions.includes("delete_record") && (
            <Button data-testid="delete-record-btn" disabled={busy || !canConfirm} onClick={() => run("record")} className="bg-red-600 hover:bg-red-700 font-bold"><Trash size={15} weight="bold" className="mr-1" />Delete this record</Button>
          )}
          {!blockedNoSuper && impact.actions.includes("delete_cascade") && (
            <Button data-testid="delete-cascade-btn" disabled={busy || !canConfirm} onClick={() => run("cascade")} className="bg-red-700 hover:bg-red-800 font-bold"><Trash size={15} weight="bold" className="mr-1" />Delete record + linked data</Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
